import { AlertPendingCertificate } from './components/Show/AlertPendingCertificate';
import { CertificatePageHeaderShow } from './components/Show/PageHeaderShow';
import { CertificateStatusValidator } from 'services/certificate';
import { DrawerApproves } from './components/Show/DrawerApproves';
import { DrawerEmissioesHistory } from './components/Show/DrawerEmissioesHistory';
import {
  ICertificate,
  ICertificateEnrollment
} from 'interfaces/course_certificate';
import { ModalCreateCertificate } from './components/Show/ModalCreateCertificate';
import { ModalNewCertificate } from './components/Show/ModalNewCertificate';
import { ModalObservation } from './components/Show/ModalObservation';
import { ShowLayout } from '../../../components/layout/layout/ShowLayout';
import { TabCertificateInfo } from './components/Show/TabCertificateInfo';
import { TabCourseInfo } from './components/Show/TabCourseInfo';
import { TabEditionHistory } from './components/Show/TabEditionHistory';
import { TabUserInfo } from './components/Show/TabUserInfo/TabUserInfo';
import { Tabs, TabsProps } from 'antd';
import { useCertificateStore } from './certificate.store';
import { useCustomSearchParams } from 'hooks/useSearchParams';
import { useEffect, useMemo } from 'react';
import { useOne, useShow } from '@refinedev/core';
import { useParams } from 'react-router-dom';

export const CertificateShow = () => {
  const { id } = useParams<{ id: string }>();

  const {
    enrollment,
    hasCertificate,
    setEnrollment,
    setCertificate,
    reset,
    drawers,
    closeEmissionsDrawer
  } = useCertificateStore();

  const { currentSearchParams: currentTab, setSearchParams } =
    useCustomSearchParams({ searchParams: 'tab' });

  // Usar useShow do Refine para buscar enrollment
  const { queryResult: enrollmentQuery } = useShow<ICertificateEnrollment>({
    resource: 'graduate/courses/enrollments',
    id: id as string
  });

  // Buscar certificado se existir
  const { data: certificateData } = useOne<ICertificate>({
    resource: 'graduate/courses/certificates',
    id: enrollment?.certificate_id as string,
    queryOptions: {
      enabled: !!enrollment?.certificate_id
    }
  });

  // Atualizar store quando dados chegarem
  useEffect(() => {
    if (enrollmentQuery.data?.data) {
      setEnrollment(enrollmentQuery.data.data);
    }
  }, [enrollmentQuery.data, setEnrollment]);

  useEffect(() => {
    if (certificateData?.data) {
      setCertificate(certificateData.data);
    }
  }, [certificateData, setCertificate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Nota: Para operações que requerem reload completo da página:
  // 1. Use useNavigation().push(window.location.pathname) nos modais
  // 2. Ou use refreshCurrentPage() da store para casos especiais

  const itemsTabs: TabsProps['items'] = useMemo(() => {
    const tabs: TabsProps['items'] = [];

    if (!enrollment) {
      return [];
    }

    // Only show certificate info tab if certificate is issued
    if (hasCertificate) {
      tabs.push({
        key: 'certificate_info',
        label: 'Informações do certificado',
        children: <TabCertificateInfo />
      });
    }

    // Always show user and course info tabs
    tabs.push(
      {
        key: 'user_info',
        label: 'Informações do aluno',
        children: <TabUserInfo />
      },
      {
        key: 'course_info',
        label: 'Informações do curso',
        children: <TabCourseInfo />
      },
      {
        key: 'edition_history',
        label: 'Histórico de edições',
        destroyInactiveTabPane: true,
        children: <TabEditionHistory />
      }
    );

    return tabs;
  }, [enrollment, hasCertificate]);

  return (
    <ShowLayout isLoading={enrollmentQuery.isLoading}>
      <CertificatePageHeaderShow />

      {enrollment && <AlertPendingCertificate />}

      <Tabs
        defaultActiveKey={currentTab || 'user_info'}
        onChange={activeKey => setSearchParams(activeKey)}
        className="custom-tabs no-edit"
        items={itemsTabs}
      />

      {/* Drawers */}
      <DrawerEmissioesHistory
        openDrawer={drawers.emissions}
        setOpenDrawer={closeEmissionsDrawer}
      />

      <DrawerApproves />

      {/* Modals */}
      <ModalNewCertificate />

      <ModalCreateCertificate />

      <ModalObservation />
    </ShowLayout>
  );
};
